<template>
  <layout :tenantId="tenantId">

    <div>
      <h1>{{ i18n.t('LoginHeader') }}</h1>
      <div class="login-reason-text">
        <p v-if="model.reason === 'LoggedOut'">{{ i18n.t('LoginReasonLoggedOut') }}</p>
        <p v-else-if="model.reason === 'SigningTimeout'">{{ i18n.t('LoginReasonSigningTimeout') }}</p>
        <p v-else-if="model.reason === 'Expired'" class="error">{{ i18n.t('LoginReasonExpiredError') }}</p>
        <p v-else-if="model.reason === 'Unauthorized'" class="error">{{ i18n.t('LoginReasonUnauthorizedError') }}</p>
        <p v-else-if="model.reason === 'InvalidScsIssuer'" class="error">{{ i18n.t('LoginReasonInvalidScsIssuerError') }}
        </p>
        <p v-else-if="model.reason === 'InvalidScsResponse'" class="error">{{ i18n.t('LoginReasonInvalidScsResponseError')
        }}</p>
        <p v-else-if="model.reason === 'UnsuccessfulSamlOperation' 
                  || model.reason === 'UnexpectedInResponseTo' 
                  || model.reason === 'SecurityTokenExpired'"
          class="error">
          {{ i18n.t('LoginReasonErrorDuringLogin') }}
        </p>
        <p v-else-if="additionalAuthenticationRequired">
          {{ i18n.t('LoginReasonAdditionalAuthRequired') }}
        </p>
        <p v-else>
          {{ i18n.t('LoginReasonDefault') }}
        </p>
      </div>
      <template v-if="page === 'loading'">
        <div class="loading" :aria-label="i18n.t('Loading')"></div>
      </template>

      <template v-else-if="page === 'selectLoginMethod'">
        <div v-for="provider in visibleAuthenticationSchemes" :key="provider.redirectUrl" class="loginmethod">
          <button class="primary" :disabled="!!provider.disabledMessage" @click="login_click(provider)">{{
            getDisplayName(provider) }}</button>
          <div v-if="provider.disabledMessage" class="SchemeDescription">
            {{ i18n.t(provider.disabledMessage) }}
          </div>
        </div>

        <div id="scsProcessHelp" v-if="scsProcessHelp" v-html="i18n.t(scsProcessHelp)"></div>

        <div v-if="!visibleAuthenticationSchemes.length" class="alert alert-warning">
          <strong>{{ i18n.t('InvalidLoginRequest') }}</strong>
          {{ i18n.t('NoVisibleExternalProviders') }}
        </div>
      </template>
      <template v-else-if="page === 'emailLoginSelected'">
        <esignOtp 
          otpType="email" 
          :tenantId="props.tenantId" 
          :signFlowId="props.signFlowId" 
          :signatureId="props.signatureId"
          :signatureRequestType="props.signatureRequestType"
          :authentication-scheme-id="selectedAutheticationMethod?.id" 
          />
      </template>
      <template v-else-if="page === 'smsLoginSelected'">
        <esignOtp 
          otpType="sms" 
          :tenantId="props.tenantId" 
          :signFlowId="props.signFlowId" 
          :signatureId="props.signatureId"
          :signatureRequestType="props.signatureRequestType"
          :authentication-scheme-id="selectedAutheticationMethod?.id" 
          />
      </template>
    </div>
  </layout>
</template>
<style scoped>
@import 'authenticate.css';
</style>

<script lang="ts" setup>
import { ref, onMounted, type PropType } from 'vue';
import { useI18n } from '../i18n-shim';
import layout from './layout.vue';
import { SignatureRequestType, AuthenticationMethod, ESchemeClient, EScsClient } from './esignclient';
import SCS from '../scs';
import esignOtp from './esignOtp.vue';
import { refreshUser } from '@/userStore';

const props = defineProps(
  {
    tenantId: {
      type: String,
      required: true
    },
    signFlowId: {
      type: String,
      required: true,
    },
    signatureRequestType: {
      type: String as PropType<SignatureRequestType>,
      required: true,
    },
    signatureId: {
      type: String,
      required: true,
    },
  }
)

function login_click(method: AuthenticationMethod) {
  selectedAutheticationMethod.value = method;
  if (method.authenticationType === 'scs') {
    initiateScsLogin(method);
  } else if (method.authenticationType === 'email') {
    
    page.value = 'emailLoginSelected';
  } else if (method.authenticationType === 'sms') {
    page.value = 'smsLoginSelected';
  }
  else {

    const returnUrl = `/${props.signatureRequestType}/${props.tenantId}/${props.signFlowId}/${props.signatureId}?lang=${i18n.locale.value}`;
    const url = `${method.redirectUrl}?ReturnUrl=${encodeURIComponent(returnUrl)}`;

    window.location.href = url;
  }
}
const selectedAutheticationMethod = ref<AuthenticationMethod>();
const additionalAuthenticationRequired = ref<boolean>(false);
const scsProcessHelp = ref<string>('');
const scsClient = new EScsClient();
async function initiateScsLogin(method: AuthenticationMethod) {
  const challenge = await scsClient.getChallenge(props.tenantId);
  scsProcessHelp.value = 'scsProcessWaitingForResponse';
  try {
    const signature = await loginWithCard(challenge);
    scsProcessHelp.value = 'scsProcessHandlingResponse';
    const response = await scsClient.scsLogin(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, method.id!, signature);

    await refreshUser(props.tenantId);
    if(response.redirectUrl) {
      window.location.href = response.redirectUrl;
    } else {
      window.location.reload();
    }
  } catch (err) {
    console.error('SCS login failed', err);
    scsProcessHelp.value = 'scsProcessResponseError';
  }
}

function loginWithCard(challenge: string): Promise<string> {
  const request = {
    //20.1.2025: Atostek doesn't seem to allow using the authentication certificate (keyusage: 'digitalsignature') with SCS api, so we use the nonRepudiation certificate instead
    selector: { keyusages: ['nonRepudiation'] },
    content: challenge,
    contentType: 'data',
    //20.1.2025: Changed SHA-256 to SHA256 to match the spec (and Atostek's implementation)
    hashAlgorithm: 'SHA256',
    //20.1.2025: Changed pkcs7 to cms to match the spec (and Atostek's implementation)
    signatureType: 'cms',
    version: '1.1',
  };

  return new Promise<string>((resolve, reject) => {
    SCS.sign((response: any) => {
      if (response.status === 'ok')
        resolve(response.signature);
      reject("SCS did not return ok status " + response.status);
    },
      request);
  });
}

const i18n = useI18n();
const visibleAuthenticationSchemes = ref<AuthenticationMethod[]>([]);
const model = ref({
  reason: new URLSearchParams(window.location.search).get('reason'),
  isScsAllowed: true,
  returnUrl: '',
  lang: ''
})

const page = ref('loading');
const client = new ESchemeClient();
onMounted(async () => {
  await showAuthenticationMethodSelection();
});

async function showAuthenticationMethodSelection () {
  const response = await client.authenticationMethodsForSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);
  visibleAuthenticationSchemes.value = response;

  if(response[0].additionalAuthenticationRequired) {
    additionalAuthenticationRequired.value = true;
  }

  selectedAutheticationMethod.value = undefined;
  scsProcessHelp.value = '';
  page.value = 'selectLoginMethod';
}

function getDisplayName(provider: AuthenticationMethod): string | undefined {
  if (provider.authenticationType === 'scs')
    return i18n.t('ScsAuthenticationDisplayName');
  if (provider.authenticationType === 'email')
    return i18n.t('EmailAuthenticationDisplayName');
  if (!provider.displayName) {
    return '';
  }
  switch (i18n.locale.value) {
    case 'en-US':
      return provider.displayName.en;
    case 'fi-FI':
      return provider.displayName.fi;
    case 'sv-FI':
      return provider.displayName.sv;
  }
  return provider.displayName.fi

}
</script>

